import Container from '@ui/container/container';
import { ICONS } from '@ui/icons/types';
import type { ResolvedEmailBlock } from '../../../templates/widgets/block-types';
import type { ButtonWidgetSize } from '../../../templates/widgets/button-widget';
import ColorPill from '../controls/color-pill';
import NumberField from '../controls/number-field';
import TextField from '../controls/text-field';
import AlignmentControl from '../controls/alignment-control';
import BorderControl from '../controls/border-control';
import BorderRadiusControl from '../controls/border-radius-control';
import BoxModelControl from '../controls/box-model-control';
import { useBoxModelStyle } from '../controls/use-box-model-style';
import { useResolvedButtonStyle } from '../controls/use-resolved-button-style';
import SizeSwitchControl from '../controls/size-switch-control';

interface ButtonStylePanelProps {
  block: ResolvedEmailBlock;
  onUpdateStyle: (key: string, value: unknown) => void;
  onUpdateProp: (key: string, value: unknown) => void;
}

export default function ButtonStylePanel({ block, onUpdateStyle, onUpdateProp }: ButtonStylePanelProps) {
  const style = block.style ?? {};
  const resolved = useResolvedButtonStyle(block.id);

  const url = (block.props.href as string | undefined) ?? '';
  const size = (style.size as ButtonWidgetSize | undefined) ?? 'medium';
  const align = (style.align as string | undefined) ?? 'center';

  const padding = useBoxModelStyle({
    blockId: block.id,
    styleValue: style.padding,
    resolvedValues: [resolved.paddingTop, resolved.paddingRight, resolved.paddingBottom, resolved.paddingLeft],
    styleKey: 'padding',
    uniformity: 'paired',
    onUpdateStyle,
  });

  return (
    <Container className="flex-column gap-16" padding="0 16px">
      <TextField
        label="URL"
        placeholder="eg: https://www.sponsor.com"
        value={url}
        onChange={(value) => onUpdateProp('href', value === '' ? undefined : value)}
        helperText="Leave blank to link to the event"
      />

      <SizeSwitchControl value={size} onChange={(value) => onUpdateStyle('size', value)} />

      <Container className="flex gap-12">
        <ColorPill
          label="Button color"
          value={(style.buttonColor as string | undefined) ?? resolved.buttonColor ?? '#000000'}
          isOverridden={style.buttonColor !== undefined}
          onChange={(value) => onUpdateStyle('buttonColor', value)}
          onReset={() => onUpdateStyle('buttonColor', undefined)}
        />
        <ColorPill
          label="Text color"
          value={(style.color as string | undefined) ?? resolved.textColor ?? '#FFFFFF'}
          isOverridden={style.color !== undefined}
          onChange={(value) => onUpdateStyle('color', value)}
          onReset={() => onUpdateStyle('color', undefined)}
        />
      </Container>

      <BorderControl
        style={style}
        onUpdateStyle={onUpdateStyle}
        resolvedColor={resolved.borderColor}
        resolvedWidth={resolved.borderWidth}
        defaultStyleValue={resolved.hasBorder ? 'solid' : 'none'}
      />

      <Container className="flex gap-12">
        <ColorPill
          label="Background color"
          value={style.background as string | undefined}
          isOverridden={style.background !== undefined}
          onChange={(value) => onUpdateStyle('background', value)}
          onReset={() => onUpdateStyle('background', undefined)}
        />
        <AlignmentControl value={align} onChange={(value) => onUpdateStyle('align', value)} />
      </Container>

      <BorderRadiusControl
        label="Rounded corners"
        blockId={block.id}
        style={style}
        onUpdateStyle={onUpdateStyle}
        resolvedValues={[
          resolved.cornerTopLeft,
          resolved.cornerTopRight,
          resolved.cornerBottomRight,
          resolved.cornerBottomLeft,
        ]}
        expandable
      />

      <BoxModelControl
        sectionLabel="Padding"
        expanded={padding.expanded}
        onToggleExpand={() => padding.setExpanded((prev) => !prev)}
        toggleIcon={ICONS.PADDING_SIDES}
        toggleLabel="padding controls"
        values={padding.values}
        onChangeSlot={padding.setSlot}
        fields={[
          { slot: 0, icon: ICONS.PADDING_TOP, ariaLabel: 'Padding top' },
          { slot: 1, icon: ICONS.PADDING_RIGHT, ariaLabel: 'Padding right' },
          { slot: 2, icon: ICONS.PADDING_BOTTOM, ariaLabel: 'Padding bottom' },
          { slot: 3, icon: ICONS.PADDING_LEFT, ariaLabel: 'Padding left' },
        ]}
        renderCollapsed={() => (
          <Container className="flex gap-12 ai-flex-end">
            <NumberField
              label="Padding"
              icon={ICONS.PADDING_VERTICAL}
              allowNegative={false}
              min={0}
              max={100}
              value={padding.values[0]}
              onChange={padding.setCollapsedFirst}
            />
            <NumberField
              icon={ICONS.PADDING_HORIZONTAL}
              allowNegative={false}
              min={0}
              max={100}
              value={padding.values[1]}
              onChange={padding.setCollapsedSecond}
            />
          </Container>
        )}
      />
    </Container>
  );
}
